<template>
  <div id="app">
    <div v-if="isVerifying" class="verifying">
      <div class="brand-header">
        <img class="brand-logo" :src="brand.logoUrl" :alt="brand.agencyName" />
        <span class="brand-name">{{ brand.agencyName }}</span>
      </div>
      <p>Verifying user...</p>
    </div>
    <div v-else-if="verificationError" class="error">
      <div class="brand-header">
        <img class="brand-logo" :src="brand.logoUrl" :alt="brand.agencyName" />
        <span class="brand-name">{{ brand.agencyName }}</span>
      </div>
      <h2>Verification Failed</h2>
      <p>{{ verificationError }}</p>
      <button @click="verifyUser">Retry</button>
    </div>
    <div v-else-if="user" class="verified">
      <!-- Connection Error Banner -->
      <div v-if="isConnected && connectionError" class="connection-banner">
        <div class="banner-content">
          <span class="banner-icon">⚠️</span>
          <span class="banner-text">
            <strong>ServiceM8 Connection Broken:</strong> Please go to <strong>Integration → Connections</strong> to reconnect.
          </span>
          <button @click="goToConnections" class="banner-button">Go to Connections</button>
        </div>
      </div>

      <!-- Tab Navigation -->
      <TabNavigation 
        :tabs="tabs"
        :activeTab="activeTab"
        :brand="brand"
        @tab-change="handleTabChange"
      />

      <!-- Tab Content Area -->
      <div class="tab-content">
        <div v-if="loadingDetails" class="loading-details">
          <p>Loading additional details from {{ brand.agencyName }}...</p>
        </div>
        <div v-else-if="detailsError" class="error-details">
          <p><strong>Error loading details:</strong> {{ detailsError }}</p>
          <button @click="loadContextDetails" class="retry-button">Retry</button>
        </div>
        
        <DashboardTab 
          v-if="activeTab === 'Dashboard'"
          :user="user"
          :userDetails="userDetails"
          :companyDetails="companyDetails"
          :locationDetails="locationDetails"
          @sync-contacts="handleSyncContacts"
          @sync-lead-to-job="handleSyncLeadToJob"
          @view-history="handleViewHistory"
          @navigate="handleNavigate"
        />
        
        <IntegrationTab 
          v-if="activeTab === 'Integration'"
          ref="integrationTab"
          :initial-sub-tab="initialSubTab"
        />
        
        <SettingsTab 
          v-if="activeTab === 'Preferences'"
          :locationId="user.locationId || user.activeLocation"
          :companyId="user.companyId"
        />

        <TroubleshootingTab
          v-if="activeTab === 'Troubleshooting'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TabNavigation from './components/TabNavigation.vue'
import DashboardTab from './components/DashboardTab.vue'
import IntegrationTab from './components/IntegrationTab.vue'
import SettingsTab from './components/SettingsTab.vue'
import TroubleshootingTab from './components/TroubleshootingTab.vue'

export default {
  name: 'App',
  components: {
    TabNavigation,
    DashboardTab,
    IntegrationTab,
    SettingsTab,
    TroubleshootingTab,
  },
  data() {
    return {
      user: null,
      brand: {
        agencyName: 'your CRM',
        logoUrl: '/fieldflow-logo.png',
      },
      isVerifying: true,
      verificationError: null,
      isConnected: false,
      connectionError: null,
      loadingDetails: false,
      userDetails: null,
      companyDetails: null,
      locationDetails: null,
      detailsError: null,
      activeTab: 'Dashboard',
      tabs: ['Dashboard', 'Integration', 'Preferences', 'Troubleshooting'],
      initialSubTab: null,
    }
  },
  provide() {
    return {
      brand: this.brand,
    }
  },
  async mounted() {
    // Check for ServiceM8 connection callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const servicem8Connected = urlParams.get('servicem8_connected');
    const servicem8Error = urlParams.get('servicem8_error');
    
    if (servicem8Connected === 'true') {
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // ConnectionStatus component will automatically refresh
      console.log('ServiceM8 connected successfully');
    } else if (servicem8Error) {
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      alert(`ServiceM8 connection failed: ${decodeURIComponent(servicem8Error)}`);
    }
    
    await this.verifyUser();
  },
  methods: {
    async verifyUser() {
      this.isVerifying = true;
      this.verificationError = null;
      this.user = null;

      try {
        const addonSession = new URLSearchParams(window.location.search).get("addon_session");
        const addonTab = new URLSearchParams(window.location.search).get("tab");
        let userData;
        if (addonSession) {
          const sessionRes = await fetch("/addon/session", {
            headers: { "x-addon-session": addonSession, "x-sso-token": addonSession },
          });
          if (!sessionRes.ok) {
            throw new Error(`Could not open ${this.brand.agencyName} settings from ServiceM8. Reopen settings.`);
          }
          userData = await sessionRes.json();
          if (addonTab === "Preferences" || addonTab === "Integration") {
            this.activeTab = addonTab;
          } else {
            this.activeTab = "Integration";
          }
        } else {
          userData = await window.ghl.getUserData();
        }
        this.user = userData;
        console.log("User verified successfully:", userData);
        
        // Check ServiceM8 connection status and load additional details
        // SSO data uses activeLocation, map it to locationId for consistency
        if (userData.activeLocation && !userData.locationId) {
          userData.locationId = userData.activeLocation;
        }
        
        if (userData.locationId || userData.activeLocation) {
          await this.checkConnectionStatus();
        }
        // Load additional details from GHL API
        await this.loadContextDetails();
        
        // Preload metadata cache in background for faster Integration tab loading
        this.preloadMetadataCache();
      } catch (error) {
        this.verificationError = error.message || `Failed to verify user. Please ensure you're accessing this app from within ${this.brand.agencyName}.`;
        console.error("User verification failed:", error);
      } finally {
        this.isVerifying = false;
      }
  },
    async checkConnectionStatus() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/status', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          this.isConnected = data.connected;
          this.connectionError = data.accountValid ? null : (data.validationError || 'Invalid connection');
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
      }
    },
    handleStatusChange(status) {
      // Handle both old format (boolean) and new format (object)
      if (typeof status === 'object' && status !== null) {
        this.isConnected = status.connected;
        this.connectionError = status.connected && !status.isValid ? 'Connection broken' : null;
      } else {
        // Legacy format support
        this.isConnected = status;
        if (status) {
          // If we just connected, check if it's actually valid to clear any existing error
          this.checkConnectionStatus();
        } else {
          this.connectionError = null;
        }
      }
    },
    handleTabChange(tab) {
      this.activeTab = tab;
    },
    handleSyncContacts() {
      this.handleNavigate({ tab: 'Integration', subTab: 'Manual Sync' });
    },
    handleSyncLeadToJob() {
      this.handleNavigate({ tab: 'Integration', subTab: 'Manual Sync' });
    },
    goToConnections() {
      this.handleNavigate({ tab: 'Integration', subTab: 'Connections' });
    },
    handleViewHistory() {
      this.handleNavigate({ tab: 'Troubleshooting' });
    },
    handleNavigate({ tab, subTab } = {}) {
      if (!tab) return;
      this.initialSubTab = tab === 'Integration' ? (subTab || null) : null;
      this.activeTab = tab;
      if (tab === 'Integration' && subTab) {
        this.$nextTick(() => {
          const integration = this.$refs.integrationTab;
          if (!integration) return;
          if (typeof integration.openSubTab === 'function') {
            integration.openSubTab(subTab);
            return;
          }
          const syncAlias = integration.subTabs?.find((name) => name === 'Manual Sync' || name === 'Initial Sync');
          integration.activeSubTab = subTab === 'Manual Sync' ? (syncAlias || subTab) : subTab;
        });
      }
    },
    async loadContextDetails() {
      console.log('[FRONTEND] loadContextDetails called', { user: this.user });
      if (!this.user) {
        console.log('[FRONTEND] loadContextDetails: No user data available');
        return;
      }
      
      this.loadingDetails = true;
      this.detailsError = null;
      try {
        const ssoToken = await this.getSSOToken();
        console.log('[FRONTEND] loadContextDetails: Fetching context details with SSO token');
        
        const response = await fetch('/api/context-details', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });
        
        console.log('loadContextDetails: Response status:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('loadContextDetails: API error response:', errorText);
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorData.message || errorMessage;
            console.error('loadContextDetails: Error details:', errorData);
          } catch (e) {
            console.error('loadContextDetails: Non-JSON error response');
            errorMessage = errorText || errorMessage;
          }
          this.detailsError = errorMessage;
          return;
        }
        
        const data = await response.json();
        console.log('[FRONTEND] loadContextDetails: Received data:', JSON.stringify(data, null, 2));
        console.log('[FRONTEND] data.company:', data.company);
        console.log('[FRONTEND] data.company type:', typeof data.company);
        console.log('[FRONTEND] data.company keys:', data.company ? Object.keys(data.company) : 'null');
        
        // User details - API returns user object directly (not wrapped)
        if (data.user && !data.user.error) {
          this.userDetails = data.user;
          console.log('[FRONTEND] userDetails set:', this.userDetails);
        } else {
          this.userDetails = null;
          console.warn('[FRONTEND] userDetails error or missing:', data.user);
        }
        
        // Company details - Backend sends { company: { company: { id: "...", name: "..." } } }
        if (data.company && !data.company.error) {
          // The backend sends: { company: { company: { id: "...", name: "..." } } }
          // So we need to unwrap: data.company.company
          if (data.company.company && typeof data.company.company === 'object') {
            this.companyDetails = data.company.company;
            console.log('[FRONTEND] Unwrapped from data.company.company');
          } else if (data.company.id || data.company.name) {
            // Fallback: maybe it's already unwrapped
            this.companyDetails = data.company;
            console.log('[FRONTEND] Using data.company directly (already unwrapped)');
          } else {
            // Last resort: use whatever we have
            this.companyDetails = data.company;
            console.warn('[FRONTEND] Company structure unexpected, using data.company as-is');
          }
          console.log('[FRONTEND] companyDetails final:', this.companyDetails);
          console.log('[FRONTEND] companyDetails.name:', this.companyDetails?.name);
          console.log('[FRONTEND] companyDetails.id:', this.companyDetails?.id);
        } else {
          this.companyDetails = null;
          console.warn('[FRONTEND] companyDetails error or missing:', data.company);
        }
        
        // Location data comes wrapped in { location: {...} } from the API
        if (data.location && !data.location.error) {
          // Already wrapped in location property, keep as is
          this.locationDetails = data.location;
          console.log('[FRONTEND] locationDetails set:', this.locationDetails);
          console.log('[FRONTEND] locationDetails has location property:', !!this.locationDetails.location);
        } else {
          this.locationDetails = null;
          console.warn('[FRONTEND] locationDetails error or missing:', data.location);
        }
        
        if (data.brand && data.brand.agencyName) {
          this.brand.agencyName = data.brand.agencyName;
          if (data.brand.logoUrl) this.brand.logoUrl = data.brand.logoUrl;
        }

        console.log('loadContextDetails: Parsed details:', {
          userDetails: this.userDetails,
          companyDetails: this.companyDetails,
          locationDetails: this.locationDetails,
          brand: this.brand,
        });
        
        // Check if all requests failed
        const allFailed = 
          (data.user?.error && !this.userDetails) &&
          (data.company?.error && !this.companyDetails) &&
          (data.location?.error && !this.locationDetails);
        
        if (allFailed) {
          this.detailsError = 'Failed to load any details. Please check the browser console for more information.';
        } else if (data.user?.error || data.company?.error || data.location?.error) {
          const errors = [];
          if (data.user?.error) errors.push(`User: ${data.user.error}`);
          if (data.company?.error) errors.push(`Company: ${data.company.error}`);
          if (data.location?.error) errors.push(`Location: ${data.location.error}`);
          console.warn('Some details failed to load:', errors);
        }
      } catch (error) {
        console.error('Error loading context details:', error);
        this.detailsError = error.message || 'Failed to load context details. Please check the browser console.';
      } finally {
        this.loadingDetails = false;
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
    formatFieldName(key) {
      // Convert camelCase to Title Case
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },
    formatValue(value) {
      if (value === null || value === undefined) {
        return 'N/A';
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (typeof value === 'object') {
        // For nested objects, show as JSON string
        return JSON.stringify(value, null, 2);
      }
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '[]';
      }
      return String(value);
    },
    async preloadMetadataCache() {
      // Preload metadata cache in background for faster Integration tab loading
      // This runs when user is verified, before they even click on Integration tab
      try {
        const cacheKey = 'servicem8_ghl_metadata';
        const cached = localStorage.getItem(cacheKey);
        
        // Check if cache exists and is valid
        const hasValidCache = cached && (() => {
          try {
            const data = JSON.parse(cached);
            const now = Date.now();
            return data.timestamp && (now - data.timestamp) < 3600000;
          } catch {
            return false;
          }
        })();
        
        // If no valid cache, start loading in background
        if (!hasValidCache) {
          console.log('Preloading metadata cache in background...');
          // Load in background without blocking
          setTimeout(async () => {
            try {
              const ssoToken = await this.getSSOToken();
              const headers = { 'x-sso-token': ssoToken };
              
              // Load all metadata in parallel
              const [catRes, badgeRes, queueRes, staffRes, existingFieldsRes, customObjectsRes, customFieldsRes, customValuesRes] = await Promise.all([
                fetch('/servicem8/categories', { headers }).catch(() => ({ ok: false })),
                fetch('/servicem8/badges', { headers }).catch(() => ({ ok: false })),
                fetch('/servicem8/queues', { headers }).catch(() => ({ ok: false })),
                fetch('/servicem8/staff', { headers }).catch(() => ({ ok: false })),
                fetch('/servicem8/existing-custom-fields', { headers }).catch(() => ({ ok: false })),
                fetch('/ghl/custom-objects', { headers }).catch(() => ({ ok: false })),
                fetch('/ghl/custom-fields', { headers }).catch(() => ({ ok: false })),
                fetch('/ghl/custom-values', { headers }).catch(() => ({ ok: false }))
              ]);
              
              const metadata = {
                timestamp: Date.now(),
                categories: [],
                badges: [],
                queues: [],
                staff: [],
                availableCustomFields: [],
                customObjects: [],
                customFields: [],
                customValues: []
              };
              
              if (catRes.ok) {
                const data = await catRes.json();
                metadata.categories = data.filter(c => c.active !== 0 && c.active !== '0');
              }
              if (badgeRes.ok) {
                const data = await badgeRes.json();
                metadata.badges = data.filter(b => b.active !== 0 && b.active !== '0');
              }
              if (queueRes.ok) {
                const data = await queueRes.json();
                metadata.queues = data.filter(q => q.active !== 0 && q.active !== '0');
              }
              if (staffRes.ok) {
                const data = await staffRes.json();
                metadata.staff = data.filter(s => s.active !== 0 && s.active !== '0');
              }
              if (existingFieldsRes.ok) {
                const data = await existingFieldsRes.json();
                metadata.availableCustomFields = data.map(f => ({
                  uuid: f.uuid,
                  name: f.display_name,
                  fieldName: f.field_name,
                  type: 'Text',
                  object: 'Job',
                  isNative: true
                }));
              }
              if (customObjectsRes.ok) {
                const data = await customObjectsRes.json();
                metadata.customObjects = data.objects || [];
              }
              if (customFieldsRes.ok) {
                const data = await customFieldsRes.json();
                metadata.customFields = data.customFields || [];
              }
              if (customValuesRes.ok) {
                const data = await customValuesRes.json();
                metadata.customValues = data.customValues || [];
              }
              
              // Save to cache
              localStorage.setItem(cacheKey, JSON.stringify(metadata));
              console.log('Metadata cache preloaded successfully');
            } catch (error) {
              console.error('Error preloading metadata cache:', error);
            }
          }, 1000); // Wait 1 second after user verification to not interfere with dashboard loading
        } else {
          console.log('Valid metadata cache already exists');
        }
      } catch (error) {
        console.error('Error checking metadata cache:', error);
      }
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.verifying {
  padding: 40px;
  font-size: 18px;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.brand-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
  background: #f8fafc;
}

.brand-name {
  font-size: 16px;
  font-weight: 700;
  color: #2c3e50;
}

.verifying p {
  color: #24ACFE;
}

.error {
  padding: 40px;
  color: #d32f2f;
}

.error h2 {
  color: #d32f2f;
  margin-bottom: 20px;
}

.error button {
  margin-top: 20px;
  padding: 10px 20px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: filter 0.3s;
}

.error button:hover {
  filter: brightness(0.9);
}

.verified {
  padding: 20px;
  background: #f4f7fb;
  min-height: 100vh;
}

.user-info {
  margin-top: 40px;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
}

.user-info h3 {
  text-align: center;
  margin-bottom: 20px;
  color: #2c3e50;
}

.user-info p {
  margin: 10px 0;
  padding: 8px;
  background-color: white;
  border-radius: 4px;
}

.user-info strong {
  color: #24ACFE;
}

.loading-details {
  text-align: center;
  padding: 20px;
  color: #666;
}

.info-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #fafafa;
  border-radius: 6px;
  border-left: 4px solid #24ACFE;
}

.info-section h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.location-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.location-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: top;
}

.location-table td:first-child {
  width: 150px;
  color: #24ACFE;
  font-weight: 500;
}

.location-table td:last-child {
  color: #2c3e50;
}

.location-table tr:last-child td {
  border-bottom: none;
}

.error-text {
  color: #d32f2f;
  font-style: italic;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.info-table thead {
  background-color: #24ACFE;
  color: white;
}

.info-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border: 1px solid #1E8CD6;
}

.info-table td {
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
}

.info-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

.info-table tbody tr:hover {
  background-color: #f0f0f0;
}

.location-section {
  margin-top: 30px;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border-left: 4px solid #24ACFE;
}

.location-section h4,
.table-section h4 {
  margin-top: 0;
  color: #2c3e50;
  font-size: 18px;
  margin-bottom: 15px;
}

.table-section {
  margin-bottom: 30px;
}

.error-details {
  padding: 15px;
  background: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 6px;
  color: #c62828;
  margin: 10px 0;
}

.error-details p {
  margin: 10px 0;
}

.retry-button {
  margin-top: 10px;
  padding: 8px 16px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: filter 0.3s;
}

.retry-button:hover {
  filter: brightness(0.9);
}

.tab-content {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px;
}

.connection-banner {
  background-color: #ffebee;
  border-bottom: 2px solid #ef5350;
  padding: 12px 20px;
  margin-bottom: 20px;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 1200px;
  margin: 0 auto;
  gap: 15px;
}

.banner-icon {
  font-size: 20px;
}

.banner-text {
  color: #c62828;
  font-size: 14px;
  line-height: 1.4;
}

.banner-button {
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s;
}

.banner-button:hover {
  background-color: #d32f2f;
}

/* Non-functional feature highlighting */
.not-implemented {
  background-color: #fff176 !important;
  border: 2px solid #fbc02d !important;
  opacity: 0.8;
  cursor: not-allowed;
  position: relative;
}

.not-implemented::after {
  content: "Not Implemented";
  position: absolute;
  top: -20px;
  left: 0;
  font-size: 10px;
  background: #fbc02d;
  color: #000;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: bold;
  z-index: 10;
}

.not-implemented:hover {
  opacity: 0.9;
}
</style>
